import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { API_URL } from "../shared/constants";
import { salesMonthLabelTr } from "../shared/utils";

export function useSalesReport() {
  const { getToken } = useAuth();
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [salesData, setSalesData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [partnersData, setPartnersData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );
  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  const fetchData = useCallback(
    async (yr) => {
      const targetYear = yr ?? year;
      setLoading(true);
      try {
        const [salesR, revenueR, partnersR] = await Promise.all([
          axios.get(
            `${API_URL}/api/analytics/sales?year=${targetYear}`,
            authH(),
          ),
          axios.get(`${API_URL}/api/analytics/revenue`, authH()),
          axios.get(`${API_URL}/api/analytics/partners`, authH()),
        ]);
        setSalesData(salesR.data);
        setRevenueData(revenueR.data);
        setPartnersData(partnersR.data);
      } catch {
        addToast("Sipariş verisi yüklenemedi", "danger");
      } finally {
        setLoading(false);
      }
    },
    [year, authH, addToast],
  );

  useEffect(() => {
    (async () => {
      setYearsLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/analytics/sales/years`,
          authH(),
        );
        const available = r.data.years || [];
        setYears(available);
        const current = new Date().getFullYear();
        const initial = available.includes(current)
          ? current
          : (available[0] ?? current);
        setYear(initial);
        await fetchData(initial);
      } catch {
        await fetchData();
      } finally {
        setYearsLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const handleYearChange = useCallback(
    (yr) => {
      setYear(yr);
      fetchData(yr);
    },
    [fetchData],
  );

  const derived = useMemo(() => {
    if (!salesData) return null;
    const monthly = salesData.monthlySales || [];

    // Monthly revenue + count series for charts
    const labels = monthly.map((m, i) => salesMonthLabelTr(i, m.year || year));
    const revenueSeries = monthly.map((m) => m.revenue || 0);
    const countSeries = monthly.map((m) => m.count || 0);
    const deviceSeries = monthly.map((m) => m.deviceCount || 0);

    // Build per-model totals across all months
    const modelMap = {};
    monthly.forEach((m) => {
      (m.models || []).forEach((mod) => {
        if (!modelMap[mod.name]) {
          modelMap[mod.name] = { name: mod.name, count: 0, revenue: 0 };
        }
        modelMap[mod.name].count += mod.count || 0;
        modelMap[mod.name].revenue += mod.revenue || 0;
      });
    });
    const modelRows = Object.values(modelMap).sort((a, b) => b.count - a.count);

    return { labels, revenueSeries, countSeries, deviceSeries, modelRows };
  }, [salesData, year]);

  return {
    years,
    year,
    handleYearChange,
    salesData,
    revenueData,
    partnersData,
    loading: loading || yearsLoading,
    derived,
    toasts,
    dismissToast,
    fetchData,
  };
}
